import {
  makeStyles,
  Theme,
  useTheme,
  createStyles,
} from "@material-ui/core/styles";
import MobileStepper from "@material-ui/core/MobileStepper";
import Button from "@material-ui/core/Button";
import KeyboardArrowLeft from "@material-ui/icons/KeyboardArrowLeft";
import KeyboardArrowRight from "@material-ui/icons/KeyboardArrowRight";
import Box from "@material-ui/core/Box";
import { PropertyInformationFormStore } from "../stores/propertyInformationFormStore";
import { InternalValuationReviewStore } from "../stores/internalValuationReviewStore";
import { useEffect, useState } from "react";
import { observer } from "mobx-react";
import { loadSubjectImage } from "../utils/propertyCompImage";
import { UploadImageButton } from "./uploadImageButton";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      flexGrow: 1,
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      height: "100%",
    },
    imgContainer: {
      flex: 1,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: "white",
      width: "100%",
      minHeight: "300px",
    },
    img: {
      height: "370px",
      maxWidth: "100%",
      display: "block",
      width: "100%",
      objectFit: "cover",
    },
  })
);

interface PicturesCarouselProps {
  propertyInformationFormStore: PropertyInformationFormStore;
  internalValuationReviewStore: InternalValuationReviewStore;
  readOnly?: boolean;
}

export const PicturesCarousel = observer(({ propertyInformationFormStore, internalValuationReviewStore, readOnly }: PicturesCarouselProps) => {
  const classes = useStyles();
  const theme = useTheme();
  const dsImages: string[] = propertyInformationFormStore.form.fields.propertyImages.value.map(img => img.url) || [];
  const { subjectImage, loanId, propertyId } = internalValuationReviewStore;

  //the photo uploaded from this form wins over the DS ones: it is the first one
  const [uploadedImage, setUploadedImage] = useState<string | null>(null);
  //DS photos that can't be displayed (expired or dead URLs)
  const [failedImages, setFailedImages] = useState<string[]>([]);

  useEffect(() => {
    if (!subjectImage) {
      setUploadedImage(null);
      return;
    }
    let active = true;
    loadSubjectImage(loanId, propertyId, subjectImage).then(url => {
      if (active) setUploadedImage(url);
    });
    return () => { active = false; };
  }, [subjectImage, loanId, propertyId]);

  const dsImagesKey = dsImages.join("|");
  useEffect(() => {
    setFailedImages([]);
    let active = true;
    dsImages.forEach(url => {
      const probe = new Image();
      probe.onerror = () => {
        if (active) setFailedImages(prev => (prev.includes(url) ? prev : [...prev, url]));
      };
      probe.src = url;
    });
    return () => { active = false; };
  }, [dsImagesKey]);

  const images = uploadedImage ? [uploadedImage, ...dsImages] : dsImages;
  const noUsableDsImages = dsImages.every(url => failedImages.includes(url));
  //only when DS has no photo (or none that loads): it is always available then, an upload replaces the previous one
  const canUpload = !readOnly && noUsableDsImages;

  const [activeStep, setActiveStep] = useState(0);
  const maxSteps = images.length;

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const currentStep = Math.min(activeStep, Math.max(maxSteps - 1, 0));
  const activeImage = images[currentStep];

  return (
    <div className={classes.root}>
      <div className={classes.imgContainer}>
        {activeImage && !failedImages.includes(activeImage) ? (
          <img className={classes.img} src={activeImage} alt="Carousel" />
        ) : (
          <Box display="flex" flexDirection="column" alignItems="center">
            <p>No Image Available</p>
            {canUpload && (
              <UploadImageButton
                internalValuationReviewStore={internalValuationReviewStore}
                testId="uploadSubjectImage"
                onUpload={file => internalValuationReviewStore.uploadSubjectImage(file) as unknown as Promise<any>}
              />
            )}
          </Box>
        )}
      </div>
      {canUpload && activeImage && !failedImages.includes(activeImage) && (
        <UploadImageButton
          internalValuationReviewStore={internalValuationReviewStore}
          testId="replaceSubjectImage"
          label="Replace photo"
          onUpload={file => internalValuationReviewStore.uploadSubjectImage(file) as unknown as Promise<any>}
        />
      )}
      <MobileStepper
        steps={maxSteps}
        position="static"
        variant="text"
        activeStep={currentStep}
        nextButton={
          <Button
            size="small"
            onClick={handleNext}
            disabled={currentStep === maxSteps - 1 || maxSteps === 0}
          >
            Next
            {theme.direction === "rtl" ? (
              <KeyboardArrowLeft />
            ) : (
              <KeyboardArrowRight />
            )}
          </Button>
        }
        backButton={
          <Button size="small" onClick={handleBack} disabled={currentStep === 0 || maxSteps === 0}>
            {theme.direction === "rtl" ? (
              <KeyboardArrowRight />
            ) : (
              <KeyboardArrowLeft />
            )}
            Back
          </Button>
        }
      />
    </div>
  );
});
